import { Portal } from '@otter-ai/den-components';
import { isServer } from '@tanstack/react-query';
import { atom, useAtomValue, useStore } from 'jotai';
import { type ReactNode, useEffect, useState } from 'react';
import { IS_DEV_ENV } from '@/constants/env';
import { LoadingModal } from '@/shared/modals/LoadingModal';
import { getJotaiStore } from '@/shared/utils/jotaiStore';

/**
 * This is the type of the function that is passed to `openDialog`.
 */
export type Render<T> = (props: {
  /**
   * Call this function to close the dialog and set the return value from openDialog.
   */
  done: (results: T) => void;
  /**
   * An AbortSignal to abort the dialog. Use it to abort `fetch` calls.
   *
   * @example
   * ```tsx
   * const abortSignal = new AbortController().signal;
   * const result = await openDialog(async ({ done, abortSignal }) => {
   *   const response = await fetch('https://api.example.com', { signal: abortSignal });
   *   // ...
   * });
   */
  abortSignal?: AbortSignal;
}) => Promise<ReactNode>;

type InlineDialog = {
  id: number;
  element: ReactNode;
};

export const inlineDialogsAtom = atom<InlineDialog[]>([]);

if (IS_DEV_ENV) {
  inlineDialogsAtom.debugLabel = 'inlineDialogsAtom';
}

let nextId = 0;
/**
 * Open a dialog (modal) inline with logic code. When the dialog is
 * finished, it will call the `done` function with the results to return.
 *
 * The `render` argument returns a promise to encourage lazy loading
 * for code splitting and to minimize bundle size.
 *
 * @example
 * ```tsx
 *  // ChangeNameDialog.tsx
 * export const ChangeNameDialog = ({ done }) => (
 *   <Dialogß onOpenChange={
 *     (open) => { if (!open)  done(null);}}
 *   >
 *     <Button onClick={() => done('Tom')}>Change your name to Tom</button>
 *   </Dialogß>
 * );
 *
 * // Example.tsx
 * function Example() {
 *   const onClick = async () => {
 *     const name = await openDialog<string | null>(
 *       async ({ done }) => {
 *         const { ChangeNameDialog } = await import('./ChangeNameDialog');
 *         return <ChangeNameDialog done={done} />;
 *       }
 *     );
 *
 *     if (!name) return;
 *     api.saveName(name);
 *   }
 * }
 * ```
 * @param render - A function that returns a promise that resolves to a ReactNode.
 * @param options - An object within optional options.
 * @param options.fallback - A ReactNode to show while the dialog is loading. Defaults to <LoadingModal />.
 * @param options.abortSignal - An AbortSignal to abort the dialog.
 * @returns A promise that resolves to the value passed to the `done` function.
 */
export function openDialog<T = void>(
  render: Render<T>,
  options?: {
    /**
     * A ReactNode to show while the dialog is loading. Defaults to <LoadingModal />.
     */
    fallback?: ReactNode;
    /**
     * An AbortSignal to abort the dialog. If the signal is aborted, the dialog will be removed and the promise will be rejected.
     */
    abortSignal?: AbortSignal;
  },
): Promise<T> {
  if (isServer) {
    throw new Error('Expect to only run on client');
  }
  const store = getJotaiStore();
  return new Promise((resolve, reject) => {
    const abortSignal = options?.abortSignal;
    if (abortSignal?.aborted) {
      reject(new DialogAborted());
    }

    const id = ++nextId;

    abortSignal?.addEventListener(
      'abort',
      () => {
        removeDialog(id, store);
        reject(new DialogAborted());
      },
      { once: true },
    );

    upsertDialog(id, options?.fallback ?? <LoadingModal />, store);

    render({
      done: (results: T) => {
        removeDialog(id, store);
        resolve(results);
      },
      abortSignal,
    }).then((rendered) => {
      if (abortSignal?.aborted) {
        return;
      }
      const element = <>{rendered}</>;
      upsertDialog(id, element, store);
    });
  });
}

function removeDialog(id: number, store: ReturnType<typeof getJotaiStore>) {
  store.set(inlineDialogsAtom, (dialogs) =>
    dialogs.filter((dialog) => dialog.id !== id),
  );
}

function upsertDialog(
  id: number,
  element: ReactNode,
  store: ReturnType<typeof getJotaiStore>,
) {
  store.set(inlineDialogsAtom, (dialogs) => [
    { id, element },
    // Remove the fallback if it was added
    ...dialogs.filter((dialog) => dialog.id !== id),
  ]);
}

/**
 * Will show the most recently added dialog
 */
export function Dialogs() {
  // TODO(sleahy): replace isReady with <ClientOnly> component
  const [isReady, setReady] = useState(false);
  const store = useStore();
  const dialogs = useAtomValue(inlineDialogsAtom, { store });

  useEffect(() => setReady(true));

  if (!isReady) {
    return null;
  }

  const topDialog = dialogs[0]?.element ?? null;
  return <Portal>{topDialog}</Portal>;
}

export class DialogAborted extends Error {
  name = 'DialogAborted';
  message = 'Dialog was aborted by AbortSignal';
}
